import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Heart, Award, Users, Target, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-20 bg-emerald-900">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=1920&h=600&fit=crop"
            alt="Nature"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Our Story</h1>
          <p className="text-xl text-emerald-200 max-w-3xl mx-auto">
            From a passion for natural healing to a mission of transforming lives through herbal wellness.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-emerald-600 font-medium mb-4">
                <Leaf className="w-5 h-5" />
                <span>Our Beginning</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Rooted in Tradition, Growing for Tomorrow
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  TouchscreenHerbal was born from a deep belief in the healing power of nature. 
                  Our founder&apos;s journey began with traditional herbal remedies passed down through 
                  generations, combined with modern wellness science.
                </p>
                <p>
                  What started as creating soursop bitters for family and friends quickly grew 
                  into a mission to share these powerful natural remedies with everyone seeking 
                  a healthier, more balanced life.
                </p>
                <p>
                  Today, we continue to honor those traditional roots while embracing innovation, 
                  bringing you the finest herbal products that are both time-tested and backed 
                  by contemporary wellness research.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=600&h=700&fit=crop"
                alt="Herbal preparation"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-emerald-600 text-white p-6 rounded-2xl shadow-xl">
                <div className="text-4xl font-bold">5+</div>
                <div className="text-emerald-200">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Leaf, title: 'Natural Purity', desc: 'Only the purest, organic ingredients make it into our products.' },
              { icon: Heart, title: 'Holistic Care', desc: 'We believe in nurturing the whole person - mind, body, and spirit.' },
              { icon: Award, title: 'Quality First', desc: 'Every product undergoes rigorous testing for safety and efficacy.' },
              { icon: Users, title: 'Community', desc: 'Building a supportive community on the path to wellness.' },
            ].map((value, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&h=500&fit=crop"
                alt="Wellness"
                className="rounded-3xl shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 text-emerald-600 font-medium mb-4">
                <Target className="w-5 h-5" />
                <span>Our Mission</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Empowering Natural Wellness
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                Our mission is to make high-quality herbal remedies accessible to everyone. 
                We believe that nature provides powerful solutions for modern health challenges, 
                and we&apos;re committed to bringing these gifts to you in their purest form.
              </p>
              <ul className="space-y-4">
                {[
                  'Source the finest organic herbs from trusted farmers',
                  'Create effective formulas backed by tradition and science',
                  'Educate and empower our community about natural wellness',
                  'Provide personalized support on your health journey',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-emerald-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Start Your Wellness Journey?
          </h2>
          <p className="text-xl text-emerald-200 mb-8">
            Explore our products or book a consultation to find the perfect herbal solution for you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/products">
              <Button size="lg" className="bg-white text-emerald-900 hover:bg-gray-100 rounded-full px-8">
                Explore Products
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8">
                Book Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
